/**
 * DataChannel management for mrmr peer connections.
 *
 * Creates pre-negotiated DataChannels for signaling, crypto, and health,
 * and provides message send/receive/routing utilities.
 */

import {
  DATACHANNEL_SIGNALING,
  DATACHANNEL_CRYPTO,
  DATACHANNEL_HEALTH,
  PROTOCOL_VERSION,
  MAX_MESSAGE_SIZE,
} from '../constants'
import type { SignalingEnvelope, SignalingMessageType } from '../types'

// ============================================================
// Types
// ============================================================

export interface DataChannels {
  signaling: RTCDataChannel
  crypto: RTCDataChannel
  health: RTCDataChannel
}

export type MessageHandler = (msg: SignalingEnvelope) => void

export type MessageHandlers = Partial<Record<SignalingMessageType, MessageHandler>>

// ============================================================
// Sequence counter (per-session, auto-incrementing)
// ============================================================

let seqCounter = 0

/**
 * Reset the sequence counter. Useful for testing.
 */
export function resetSeqCounter(): void {
  seqCounter = 0
}

// ============================================================
// DataChannel creation
// ============================================================

/**
 * Create three pre-negotiated DataChannels on an RTCPeerConnection.
 *
 * All channels use `negotiated: true` with fixed IDs so both peers
 * create matching channels without SDP renegotiation.
 */
export function createDataChannels(pc: RTCPeerConnection): DataChannels {
  const signaling = pc.createDataChannel(DATACHANNEL_SIGNALING.label, {
    id: DATACHANNEL_SIGNALING.id,
    negotiated: DATACHANNEL_SIGNALING.negotiated,
    ordered: DATACHANNEL_SIGNALING.ordered,
    protocol: DATACHANNEL_SIGNALING.protocol,
  })

  const crypto = pc.createDataChannel(DATACHANNEL_CRYPTO.label, {
    id: DATACHANNEL_CRYPTO.id,
    negotiated: DATACHANNEL_CRYPTO.negotiated,
    ordered: DATACHANNEL_CRYPTO.ordered,
    protocol: DATACHANNEL_CRYPTO.protocol,
  })

  const health = pc.createDataChannel(DATACHANNEL_HEALTH.label, {
    id: DATACHANNEL_HEALTH.id,
    negotiated: DATACHANNEL_HEALTH.negotiated,
    ordered: DATACHANNEL_HEALTH.ordered,
    maxRetransmits: DATACHANNEL_HEALTH.maxRetransmits,
  })

  return { signaling, crypto, health }
}

// ============================================================
// Envelope factory
// ============================================================

/**
 * Create a SignalingEnvelope with auto-incrementing sequence number.
 */
export function createEnvelope(
  type: string,
  from: string,
  payload: unknown,
): SignalingEnvelope & { payload: unknown } {
  return {
    v: PROTOCOL_VERSION as 1,
    type,
    ts: Date.now(),
    from,
    seq: seqCounter++,
    payload,
  }
}

// ============================================================
// Send
// ============================================================

/**
 * Serialize and send a SignalingEnvelope over a DataChannel.
 * Throws if the serialized message exceeds MAX_MESSAGE_SIZE.
 */
export function sendMessage(channel: RTCDataChannel, msg: SignalingEnvelope): void {
  const data = JSON.stringify(msg)
  if (data.length > MAX_MESSAGE_SIZE) {
    throw new Error(
      `Message exceeds max size: ${data.length} > ${MAX_MESSAGE_SIZE}`,
    )
  }
  channel.send(data)
}

// ============================================================
// Message routing
// ============================================================

/**
 * Register message handlers on all three DataChannels.
 *
 * Incoming messages are parsed as JSON, validated for the `type` field,
 * and routed to the matching handler. Unknown types are silently ignored.
 */
export function onMessage(
  channels: DataChannels,
  handlers: MessageHandlers,
): void {
  const listener = (event: MessageEvent) => {
    try {
      const msg = JSON.parse(event.data as string) as SignalingEnvelope
      if (!msg.type) return
      const handler = handlers[msg.type as SignalingMessageType]
      if (handler) handler(msg)
    } catch {
      // Malformed JSON — drop silently
    }
  }

  channels.signaling.addEventListener('message', listener)
  channels.crypto.addEventListener('message', listener)
  channels.health.addEventListener('message', listener)
}
